import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { Group } from '@/interfaces/Group'
import { Base } from '@/interfaces/Base'
import { GroupTag } from '@/interfaces/GroupTag'
import { BaseDetail } from '@/interfaces/BaseDetail'
import { ProductDetail } from '@/models/ProductDetail'
import { Charm } from '@/models/Charm'

interface ApiError {
  errorCode: number;
  message: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
}

declare module 'vue/types/vue' {
  interface Vue {
    $apiService: Api;
  }
}

export class Api {
  client: AxiosInstance;

  constructor () {
    this.client = axios.create({
      baseURL: process.env.VUE_APP_BASE_URL || '',
      responseType: 'json',
      method: 'GET'
    })
  }

  private async tryGetData<T> (url: string, params?: { limit: number; offset: number }): Promise<T | ApiError> {
    try {
      let config: AxiosRequestConfig | undefined

      if (params) {
        config = {
          params: {
            filter: JSON.stringify({
              limit: params.limit,
              offset: params.offset
            })
          }
        }
      }

      const response = await this.client.get<T>(url, config)
      return response.data
    } catch (e: any) {
      // TODO: handle errors
      return { message: e.message, errorCode: 500 }
    }
  }

  private async tryPostData<T> (url: string, data?: any): Promise<T | ApiError> {
    try {
      const config: AxiosRequestConfig = {
        method: 'POST',
        baseURL: process.env.VUE_APP_ECOMMERCE_URL || '',
        responseType: 'json'
      }

      const response = await this.client.post<T>(url, data, config)
      return response.data
    } catch (e: any) {
      // TODO: handle errors
      return { message: e.message, errorCode: 500 }
    }
  }

  private isError (entity: any | ApiError) {
    return 'errorCode' in entity
  }

  async getGroups (): Promise<Group[] | null> {
    const groups = await this.tryGetData<Group[]>('groups')

    if (this.isError(groups)) {
      // TODO: handle group error
      return null
    }

    return groups as Group[]
  }

  async getBases (filter: Group | GroupTag, offset: number, limit: number): Promise<Base[] | null> {
    const path = 'groupId' in filter ? 'groups/' + filter.groupId : 'tags/' + filter.tagId

    const bases = await this.tryGetData<Base[]>(
      path,
      { offset, limit }
    )

    if (this.isError(bases)) {
      // TODO: handle bases error
      return null
    }

    return bases as Base[]
  }

  async getBaseDetails (productId: number): Promise<BaseDetail | null> {
    const baseDetail = await this.tryGetData<BaseDetail>('base/' + productId)

    if (this.isError(baseDetail)) {
      // TODO: handle base detail error
      return null
    }

    return baseDetail as BaseDetail
  }

  async addProductsToCart (products: ProductDetail[]): Promise<ApiResponse | null> {
    const productsUniques: any = {}

    for (const product of products) {
      const isSimpleProduct = product.idPrestashopAttribute === null || product.idPrestashopProduct === product.idPrestashopAttribute

      const uniqueKey = product.idPrestashopProduct +
        (isSimpleProduct ? '' : '-' + product.idPrestashopAttribute)

      if (uniqueKey in productsUniques) {
        const record = productsUniques[uniqueKey]
        record.quantity++
      } else {
        productsUniques[uniqueKey] = {
          id_product: product.idPrestashopProduct,
          id_product_attribute: isSimpleProduct ? null : product.idPrestashopAttribute,
          quantity: 1
        }
      }
    }

    const data: any = {
      productsToAdd: Object.values(productsUniques)
    }

    const response = await this.tryPostData<ApiResponse>('configuratore?ajax=true&action=addToCart', data)

    if (this.isError(response)) {
      // TODO: handle addToCart error
      return null
    }

    return response as ApiResponse
  }

  getCompositionBody (baseId: number, charms: Charm[]) {
    const products: any[] = []
    for (const charm of charms) {
      products.push({
        x: charm.x,
        y: charm.y,
        id: charm.id
      })
    }

    return {
      baseId: baseId,
      charms: products
    }
  }

  async getCompositionImage (baseId: number, charms: Charm[]) {
    const body = this.getCompositionBody(baseId, charms)
    const blob = await this.downloadFile('preview', body)
    return blob ? window.URL.createObjectURL(blob) : null
  }

  async getCompositionPdf (baseId: number, charms: Charm[], pdfContent: any) {
    const body = {
      pdf: pdfContent,
      ...this.getCompositionBody(baseId, charms)
    }
    return await this.openPdf('composizione.pdf', 'pdf', body)
  }

  async downloadFile (url: string, data: any, blobOptions?: {type: string}): Promise<Blob | null> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      responseType: 'blob'
    }

    const response = await this.client.post<any>(url, data, config)
    return response ? new Blob([response.data], blobOptions) : null
  }

  async openPdf (name: string, url: string, data: any) {
    const dataBlob = await this.downloadFile(url, data, { type: 'application/pdf' })
    if (dataBlob) {
      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob
      if (window.navigator && 'msSaveOrOpenBlob' in window.navigator) {
        const navigator = window.navigator as any;
        navigator.msSaveOrOpenBlob(dataBlob)
        return
      }

      const dataUrl = window.URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = dataUrl
      link.setAttribute('download', name)
      document.body.appendChild(link)

      link.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(dataUrl)
      }, 100)
    }
  }
}
